import type {
  Hotkey,
  HotkeyIndicatorCharacter,
  HotkeyMetaKey,
} from "@v1/components/common/designSystem/utils/hotkeys";

import * as React from "react";

import {
  getHotkeyData,
  HotkeyMetaKeyToLabel,
} from "@v1/components/common/designSystem/utils/hotkeys";
import { isMacintosh } from "@v1/components/common/designSystem/utils/platform";
import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { Badge } from "@v1/components/designSystem/badge/Badge";

const FancyHotkeyMetaKeyToLabel = {
  ...HotkeyMetaKeyToLabel,
  meta: {
    mac: (HotkeyMetaKeyToLabel.meta as { mac: string }).mac,
    windows: <span style={{ fontSize: "6px", fontWeight: "bold" }}>CTRL</span>,
  },
  esc: {
    mac: (HotkeyMetaKeyToLabel.esc as { mac: string }).mac,
    windows: <span style={{ fontSize: "6px", fontWeight: "bold" }}>ESC</span>,
  },
};

/**
 * @deprecated Use HotkeyIndicator from @replohq/lattice/components/hotkey-indicator/HotkeyIndicator instead.
 */
export const HotkeyIndicator: React.FC<{
  title?: string;
  hotkey: Hotkey;
  className?: string;
  badgeBackgroundColor?: string;
}> = ({ hotkey, title, className, badgeBackgroundColor }) => {
  const hotkeyData = getHotkeyData(hotkey);
  return (
    <div
      className={twMerge(
        "flex flex-row items-center justify-center gap-1 text-xs",
        className,
      )}
    >
      <span className="block">{title ?? hotkeyData.displayName}</span>
      <div
        className={twMerge(
          "flex flex-row items-center gap-1",
          !badgeBackgroundColor && "gap-[2px]",
        )}
      >
        {getHotkeyLabel(hotkeyData.indicatorCharacters).map(
          ({ id, display }) => (
            <>
              {badgeBackgroundColor ? (
                <Badge
                  key={id}
                  type="icon"
                  icon={display}
                  isFilled
                  UNSAFE_className={twMerge(
                    "text-muted",
                    badgeBackgroundColor
                      ? badgeBackgroundColor
                      : "bg-menu-item-hover",
                  )}
                />
              ) : (
                <div key={id} className="text-placeholder text-xs">
                  {display}
                </div>
              )}
            </>
          ),
        )}
      </div>
    </div>
  );
};

function getHotkeyLabel(hotkey: HotkeyIndicatorCharacter[]) {
  return hotkey.map((character) => {
    const label = FancyHotkeyMetaKeyToLabel[character as HotkeyMetaKey];
    if (label) {
      if (typeof label === "string") {
        return { id: character, display: label };
      }
      return {
        id: character,
        display: !isMacintosh() ? label.windows : label.mac,
      };
    }
    return { id: character, display: character.toUpperCase() };
  });
}
